import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import { EditIcon, DeleteIcon } from './Icons';

const today = () => new Date().toISOString().split('T')[0];
const EMPTY = { id: null, effective_date: today(), rate_pct: '', notes: '' };

// A money market symbol's 7-day yield history, newest first, with single-entry
// add/edit/delete for corrections. Bulk history comes from the file import on the
// Data page. `onChanged` fires after any change so the caller can refresh the
// symbol's current rate.
export default function MoneyMarketRates({ symbol, onClose, onChanged }) {
  const [rates, setRates] = useState(null);
  const [form, setForm] = useState(null); // null = not editing
  const [error, setError] = useState('');

  const load = () => api.getMoneyMarketRates(symbol).then(setRates).catch(e => { setRates([]); setError(e.message); });
  useEffect(() => { load(); }, [symbol]);

  const save = async () => {
    const rate = parseFloat(form.rate_pct);
    if (!form.effective_date) return setError('Enter a date.');
    if (!(rate >= 0)) return setError('Enter the 7-day yield as a percentage, e.g. 3.49.');
    try {
      if (form.id) {
        await api.updateMoneyMarketRate(form.id, { effective_date: form.effective_date, rate_pct: rate, notes: form.notes });
      } else {
        await api.addMoneyMarketRate({ symbol, effective_date: form.effective_date, rate_pct: rate, notes: form.notes });
      }
    } catch (e) {
      return setError(e.message);
    }
    setForm(null);
    setError('');
    load();
    if (onChanged) onChanged();
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the ${symbol} rate for ${r.effective_date}?`)) return;
    try {
      await api.deleteMoneyMarketRate(r.id);
    } catch (e) {
      return setError(e.message);
    }
    setError('');
    load();
    if (onChanged) onChanged();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <div className="modal-title"><span className="mono">{symbol}</span> 7-Day Yield History</div>
          {!form && (
            <button className="btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => { setForm(EMPTY); setError(''); }}>+ Add Rate</button>
          )}
        </div>
        <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
          One rate per day. A day with no entry uses the most recent earlier rate. Load full history from a file on the Data page.
        </div>

        {form && (
          <div style={{ display: 'grid', gridTemplateColumns: '150px 110px 1fr', gap: 10, marginBottom: 12, alignItems: 'end' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Date</label>
              <input type="date" value={form.effective_date} onChange={e => setForm({ ...form, effective_date: e.target.value })} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Yield %</label>
              <input type="number" min="0" step="0.01" value={form.rate_pct} onChange={e => setForm({ ...form, rate_pct: e.target.value })} placeholder="3.49" />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Notes (optional)</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10 }}>
              <button className="btn" onClick={save}>{form.id ? 'Save Changes' : 'Save'}</button>
              <button className="btn-ghost" onClick={() => { setForm(null); setError(''); }}>Cancel</button>
            </div>
          </div>
        )}

        {error && <div className="loss" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {rates === null ? <div className="muted">Loading...</div> : (
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr><th>Date</th><th style={{ textAlign: 'right' }}>7-Day Yield</th><th>Notes</th><th></th></tr>
              </thead>
              <tbody>
                {rates.map(r => (
                  <tr key={r.id}>
                    <td className="mono">{r.effective_date}</td>
                    <td className="mono amount">{r.rate_pct.toFixed(2)}%</td>
                    <td className="muted" style={{ fontSize: 12 }}>{r.notes}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="icon-btn-sm" style={{ marginRight: 4 }} title="Edit" aria-label="Edit"
                        onClick={() => { setForm({ id: r.id, effective_date: r.effective_date, rate_pct: r.rate_pct, notes: r.notes || '' }); setError(''); }}>
                        <EditIcon />
                      </button>
                      <button className="icon-btn-sm" title="Delete" aria-label="Delete" onClick={() => remove(r)}><DeleteIcon /></button>
                    </td>
                  </tr>
                ))}
                {rates.length === 0 && (
                  <tr><td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 24 }}>No rates yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
